<template>
  <div class="w-full p-5 md:w-112">
    <!-- User Limit Upgrade Banner -->
    <div v-if="isAtUserLimit" class="upgrade-overlay" data-testid="invite-modal-upgrade-banner">
      <div class="upgrade-content">
        <div class="upgrade-icon">
          <UpgradeButton @click="goToPricing" />
        </div>
        <h3 class="upgrade-title">Upgrade to invite team members</h3>
        <p class="upgrade-description">
          Your current plan supports {{ userLimitValue }} user{{ userLimitValue === 1 ? '' : 's' }}. Upgrade to Collaborate to add team members.
        </p>
        <Button type="solid" variant="blue" @click.prevent="goToPricing" data-testid="invite-modal-view-plans-btn"> View Plans </Button>
      </div>
    </div>

    <!-- Regular Invite Form (shown when not at limit, or disabled when at limit) -->
    <div :class="{ 'form-disabled': isAtUserLimit }">
      <p class="mb-4 mr-9 font-bold dark:text-zinc-300">Invited users will have access to shared collections and content limited to the team.</p>
      <label for="inviteMembers" class="block mb-2 dark:text-zinc-500">Invite people via email</label>
      <div class="mb-2">
        <Chips placeholder="Add People" v-model="newInvitations" pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$" :disabled="isAtUserLimit" />

        <template v-for="(err, idx) in errors" :key="idx">
          <p v-if="err.type === 'no_seats_left'" class="flex items-start gap-2 mt-2 text-sm text-orange-600 dark:text-orange-400">
            <Icon name="general.exclamationCircleSolid" size="sm" class="flex-shrink-0 mt-0.5" />
            <span>
              {{ err.detail }}
              <a :href="upgradePlanUrl" class="font-semibold underline hover:no-underline">Upgrade your plan</a>
              to invite team members.
            </span>
          </p>
          <p v-else class="flex items-start gap-2 mt-2 text-sm text-red-500">
            <Icon name="general.exclamationCircleSolid" size="sm" class="flex-shrink-0 mt-0.5" />
            <span>{{ err.detail }}</span>
          </p>
        </template>
      </div>
      <div class="mb-4">
        <Button type="solid" variant="blue" icon="general.paperPlane" @click.prevent="sendInvitations" :disabled="newInvitations?.length === 0 || isAtUserLimit"
          >Invite</Button
        >
      </div>
      <template v-if="invitedEmails.length > 0">
        <p class="mb-2 dark:text-zinc-500">Invited people</p>
        <Chips v-model="invitedEmails" mode="readonly" />
      </template>
      <div class="mb-2">
        <label for="inviteLink" class="block mb-2 dark:text-zinc-500">Or share this link with your team</label>
        <input
          type="text"
          id="inviteLink"
          @focus="(e: any) => e.target.select()"
          class="w-full px-3 overflow-y-hidden bg-white bg-no-repeat border outline-none rounded-10 dark:bg-zinc-800 dark:text-zinc-500 h-9 border-slate-300 dark:border-zinc-700 focus:border-blue-500 focus:ring focus:ring-blue-500/50 active:border-slate-600"
          :disabled="newInvitations?.length === 0 || isAtUserLimit"
          :value="inviteLink"
        />
      </div>
      <div class="mb-4">
        <Button type="solid" variant="blue" icon="general.copy" @click.prevent="copy()" :disabled="isAtUserLimit">Copy link to clipboard</Button>
      </div>
      <div v-if="showMemberDiscount && !isAtUserLimit" class="p-2 border border-slate-300 dark:border-zinc-700 rounded-10">
        <p class="inline-block px-2.5 py-1 mb-2 text-white rounded-full bg-gradient-to-tr from-orange-500 to-red-500">20% discount</p>
        <p class="dark:text-zinc-300">
          Invite {{ membersUntillDiscount }} more member{{ membersUntillDiscount == 2 ? 's' : '' }} and reduce your price to <s>$9.95</s> $8/member.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ref, Ref, watch, computed, toRaw } from 'vue';
import { useClipboard } from '@vueuse/core';
import { useToast } from '@/composables/useToast';
import { useOrganizationStore, DISCOUNT_MAX_SEATS } from '@/store/useOrganizationStore';
import { useUserSeats } from '@/composables/useUserSeats';
import { Chips } from '@/storybook/form';
import { Button } from '@/storybook/button';
import { Icon } from '@/storybook/icon';
import { UpgradeButton } from '@/storybook/templates/limits';

const orgStore = useOrganizationStore();
const { invitationUrl, showMemberDiscount, membersCount } = storeToRefs(orgStore);
const { success, error } = useToast();
const { isAtUserLimit, userLimit } = useUserSeats();

const userLimitValue = computed(() => userLimit.value ?? 0);

const upgradePlanUrl = '/account/plans?utm_source=invite_modal&utm_medium=upgrade_cta&utm_campaign=invite_members';

const goToPricing = () => {
  window.location.href = upgradePlanUrl;
};

const newInvitations: Ref<string[]> = ref([]);
const invitedEmails: Ref<string[]> = ref([]);
const errors: Ref<{ detail: string; type: string }[]> = ref([]);
const inviteLink = ref(invitationUrl);

const inviteString = computed(() => inviteLink.value ?? '');
const { copy, copied } = useClipboard({ source: inviteString });

const emit = defineEmits(['close']);

const sendInvitations = () => {
  errors.value = [];
  orgStore
    .sendOrgInvitations(toRaw(newInvitations.value))
    .then(resp => {
      success(resp.message ? resp.message : 'Invitations sent');

      newInvitations.value = [];
      return emit('close');
    })
    .catch(e => {
      // API returns errors as { errors: [{ title, detail, type }] }
      if (e?.errors?.length > 0) {
        e.errors.forEach((err: { title?: string; detail?: string; type?: string }) => {
          if (err.detail) {
            errors.value.push({ detail: err.detail, type: err.type ?? '' });
          }
        });
      }

      if (errors.value.length === 0) {
        error('Cannot send invitations');
      }
    });
};

const membersUntillDiscount = computed(() => DISCOUNT_MAX_SEATS - membersCount.value);

watch(copied, res => {
  if (!res) {
    return;
  }

  success('Invite link copied to clipboard', { icon: 'general.copy' });
});
</script>

<style scoped>
.upgrade-overlay {
  @apply mb-6 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700/50 rounded-lg;
}

.upgrade-content {
  @apply flex flex-col items-center text-center gap-3;
}

.upgrade-icon {
  @apply mb-1;
}

.upgrade-title {
  @apply text-lg font-semibold text-zinc-900 dark:text-zinc-100;
}

.upgrade-description {
  @apply text-sm text-zinc-600 dark:text-zinc-400 mb-2;
}

.form-disabled {
  @apply opacity-50 pointer-events-none;
}
</style>
